import React, { useEffect, useState } from 'react';

import CardDialog from '~/components/CardDialog';
import IconButton from '~/components/core/Atomic/Buttons/IconButton';
import Tooltip from '~/components/core/Atomic/Tooltip';
import LoadingSwitch from '~/components/core/Loading/LoadingSwitch';
import GenericTemplateContentContainer from '~/components/GenericTemplates/GenericTemplatesConfiguration/GenericTemplateCard/GenericTemplateContentContainer';
import { MOI_EMAIL_TEMPLATES_ROUTES } from '~/components/GenericTemplates/utils/routes';
import { PencilIcon } from '~/components/icons';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import useDataFetcher from '~/components/useDataFetcher';
import { reportAxiosError } from '~/Utils';

import { saveMoiEmailTemplate } from './moiEmailApi';
import MoiEmailTemplateBuilderContainer from './MoiEmailTemplateBuilderContainer';
import type { MoiEmailTemplate, MoiEmailTemplateFormValues } from './types';
import { moiEmailTemplateToFormValues } from './types';

interface MoiEmailTemplateCardContainerProps {
  template: MoiEmailTemplate;
  otherTemplates: MoiEmailTemplate[];
  reloadTable: () => Promise<void>;
}

const MoiEmailTemplateCardContainer: React.FC<MoiEmailTemplateCardContainerProps> = ({
  template,
  otherTemplates,
  reloadTable,
}) => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { organization } = useSysconfig();
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [containerContentKey, setContainerContentKey] = useState(0);

  // The synthetic Default has no id and cannot be fetched; it renders the in-code default content
  // (which does not change until first saved). Real templates are fetched by id like the generic card.
  const templateUrl =
    !!template.id && template.id > 0 ? MOI_EMAIL_TEMPLATES_ROUTES.GET_TEMPLATE(organization.id, template.id) : null;
  const { data, isLoading, isError, reloadData } = useDataFetcher<MoiEmailTemplate>(templateUrl, undefined, {
    shouldFetch: templateUrl !== null,
  });

  const displayedTemplate = templateUrl !== null ? data : template;

  // The Editor initialises its content once, so remount it whenever the fetched template changes.
  useEffect(() => {
    setContainerContentKey((key) => key + 1);
  }, [data]);

  const handleUpdate = async (values: MoiEmailTemplateFormValues) => {
    try {
      await saveMoiEmailTemplate(organization.id, values);
      // Only the fetched (materialized) template has a card-level fetch to refresh; the synthetic
      // Default has none and relies on the parent list reload.
      if (templateUrl !== null) {
        await reloadData();
      }
      await reloadTable();
    } catch (error) {
      await reportAxiosError(error);
      throw error;
    }
  };

  return (
    <LoadingSwitch isLoading={templateUrl !== null && isLoading} isError={templateUrl !== null && isError}>
      <CardDialog
        containerClassName="overflow-y-auto border-1 border-slate-600 border-solid rounded-md mx-12"
        noCardTitle
      >
        <div className="m-12 flex items-center justify-end">
          <Tooltip title="Edit">
            <IconButton onClick={() => setIsEditDialogOpen(true)} className="hover:bg-transparent">
              <PencilIcon className="cursor-pointer hover:stroke-teal-700" />
            </IconButton>
          </Tooltip>
        </div>
        {displayedTemplate ? (
          <GenericTemplateContentContainer
            key={containerContentKey}
            titleTemplate={displayedTemplate.title_template ?? undefined}
            bodyTemplate={displayedTemplate.body_template ?? ''}
          />
        ) : null}
        {isEditDialogOpen && displayedTemplate ? (
          <MoiEmailTemplateBuilderContainer
            isEdit
            initialValues={moiEmailTemplateToFormValues(displayedTemplate)}
            otherTemplates={otherTemplates}
            handleClose={() => setIsEditDialogOpen(false)}
            handleSubmit={handleUpdate}
          />
        ) : null}
      </CardDialog>
    </LoadingSwitch>
  );
};

export default MoiEmailTemplateCardContainer;
